import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { QRCodeSVG } from 'qrcode.react';
import Button from 'components/button/button';
import Copy from 'components/copy';
import { AppUI } from '@seedlang/state';
import TextDisplay from 'components/text_display';
import Text from 'components/text';
import { Constants } from '@seedlang/constants';
import Stack, { gaps } from 'components/layout/stack';
import AdminOnly from 'utils/admin_only';
import Spinner from 'components/spinner';
import NoAccessButton from 'components/worksheet/no_access_button';

const Wrapper = styled.div`
  h3 {
    font-size: 18px!important;
    line-height: 18px!important;
    font-weight: bold;
    margin-bottom: 5px;
    margin-top: 30px;
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const AppIcon = styled.div`
  padding: 10px;
  cursor: pointer;
  background: ${props => props.background};
  border-radius: 10px;
  &:hover {
    background: #DDD;
  }
`;

const Name = styled.div`
  font-size: 11px;
  width: 50px;
  line-height: 13px;
  text-align: center;
  margin-top: 5px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const Section = styled.div`
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
`;

const InputWrapper = styled.div`
  input {
    max-width: 250px;
    border-radius: 5px;
    height: 40px;
  }
`;

const TabRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${gaps.md};
  gap: ${gaps.xs};

  @media (min-width: 1000px) {
    flex-direction: row;
    border-bottom: 1px solid #CCC;
    flex-wrap: wrap;
  }
`;

const Tab = styled.div`
  padding: 10px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  color: ${props => props.selected ? props.borderColor : '#A9A9A9'};
  border-left: 4px solid ${props => props.selected ? props.borderColor : 'transparent'};
  text-transform: uppercase;
  white-space: nowrap;
  transition: all 0.2s ease-in-out;

  @media (min-width: 1000px) {
    border-left: none;
    border-bottom: 4px solid ${props => props.selected ? props.borderColor : 'transparent'};
  }
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: min-content;
  padding: 0 4px;
`;

const TabImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const Note = styled.div`
  font-size: 14px;
  line-height: 16px;
  font-style: ${props => props.fontStyle ? props.fontStyle : 'italic'};
  margin-bottom: ${props => props.marginBottom};
  margin-top: ${props => props.marginTop};
`;

const InnerWrapper = styled.div`
  height: ${props => props.hasAccess ? null : '500px'};
  mask-image: ${props => props.hasAccess ? null : 'linear-gradient(to bottom,black,25%,transparent)'};
  pointer-events: ${props => props.hasAccess ? 'auto' : 'none'};
  user-select: ${props => props.hasAccess ? 'auto' : 'none'};
  overflow: hidden;
`;

const RelativeWrapper = styled.div`
  position: relative;
`;

const NoAccessWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 25%;
  width: 100%;
  z-index: 1;
`;

const APPS = [
  {
    slug: 'apple',
    name: 'Apple Podcasts',
    linkPrefix: 'podcast://',
  },
  {
    slug: 'overcast',
    name: 'Overcast',
    linkPrefix: 'overcast://x-callback-url/add?url=https://',
  },
  {
    slug: 'podcast_addict',
    name: 'Podcast Addict',
    linkPrefix: 'podcastaddict://',
  },
  {
    slug: 'pocket_casts',
    name: 'Pocket Casts',
    linkPrefix: 'pktc://subscribe/',
  },
  {
    slug: 'castro',
    name: 'Castro',
    linkPrefix: 'castros://subscribe/',
  },
  {
    slug: 'downcast',
    name: 'Downcast',
    linkPrefix: 'downcast://',
  },
  {
    slug: 'android',
    name: 'Android',
    linkPrefix: 'pcast://',
  },
  {
    slug: 'spotify',
    name: 'Spotify',
  },
  {
    slug: 'other_apps',
    name: 'Other Apps',
  },
];

@observer
class RssFeedIndex extends React.Component {
  @observable selectedApp = 'apple';
  @observable selectedPodcastId = null;

  componentDidMount() {
    if (!AppUI.podcastStore.hasIndexData) {
      AppUI.podcastStore.getIndex();
    }

    if (this.props.selectedPodcastId) {
      this.selectedPodcastId = this.props.selectedPodcastId;
    }
  }

  @computed get selectedPodcast() {
    return AppUI.podcastStore.indexData.find(item => item.id === this.selectedPodcastId)
      ?? AppUI.podcastStore.indexData[0];
  }

  @computed get selectedUserPodcast() {
    return AppUI.user.userPodcasts.find(item => item.podcast?.id === this.selectedPodcast?.id)
      ?? null;
  }

  @computed get currentApp() {
    return APPS.find(item => item.slug === this.selectedApp);
  }

  @computed get currentAppUrl() {
    if (!this.selectedUserPodcast) return null;
    const urlWithoutProtocol = this.selectedUserPodcast.rssFeedUrl.replace('https://', '').replace('http://', '');
    return `${this.currentApp.linkPrefix}${urlWithoutProtocol}`;
  }
  openInSelectedApp = () => {
    window.location.href = this.currentAppUrl;
  }

  @computed get message() {
    return AppUI.site?.podcastSettingsMessage || Constants.PODCAST_SETTINGS_MESSAGE;
  }

  @computed get blockedUntilMigrated() {
    return this.selectedPodcast?.blockIfNotMigrated && AppUI.shouldBlockPerksForMigration;
  }

  render() {
    if (AppUI.podcastStore.hasOutstandingRequests) {
      return <Spinner />;
    }

    return (
      <Wrapper>
        <div>
          <Note fontStyle='none'>
            <TextDisplay markdown={this.message} />
          </Note>
          <TabRow>
            {
              AppUI.podcastStore.indexData.map(item => {
                return (
                  <Tab
                    key={item.id}
                    onClick={() => this.selectedPodcastId = item.id}
                    selected={this.selectedPodcast?.id === item.id}
                    borderColor={AppUI.site.accentColor}
                  >
                    <TabContent>
                      {item.podcastImage?.image && (
                        <TabImage
                          src={item.podcastImage.image.url}
                          alt={`Cover image for ${item.title}`}
                        />
                      )}
                      {item.title}
                    </TabContent>
                  </Tab>
                );
              })
            }
          </TabRow>
          <RelativeWrapper>
            {
              (!this.selectedUserPodcast || this.blockedUntilMigrated) &&
                  <NoAccessWrapper>
                    <NoAccessButton migrate={this.blockedUntilMigrated} />
                  </NoAccessWrapper>
            }
          <InnerWrapper hasAccess={this.selectedUserPodcast}>
            <Section>
              {
                this.selectedPodcast?.podcastImage?.image &&
                  <img
                    alt=''
                    width='200px'
                    style={{borderRadius: '20px'}}
                    src={this.selectedPodcast.podcastImage.image.url}
                  />
              }
            </Section>
            {
              this.selectedUserPodcast &&
                <Section>
                  <AdminOnly>
                    <Stack gap='sm' alignItems='center'>
                      <Button
                        onClick={() => {
                          window.open(this.selectedUserPodcast.rssFeedUrl, '_blank');
                        }}
                        background={AppUI.site.accentColor}
                      >
                        Open RSS feed
                      </Button>
                      <Note>You are seeing this because you are an admin</Note>
                    </Stack>
                  </AdminOnly>
                </Section>
            }
              <Section>
                <h3>Select your Podcast App</h3>
                <IconWrapper>
                  {
                    APPS.map(item => {
                      return (
                        <AppIcon
                          key={item['slug']}
                          onClick={() => this.selectedApp = item['slug']}
                          background={this.selectedApp === item['slug'] ? '#DDD' : '#FFF'}
                        >
                          <img
                            src={`/images/${item['slug']}.svg`}
                            width='50px'
                            height='50px'
                            alt=''
                            style={{borderRadius: '10px'}}
                          />
                          <Name>{item['name']}</Name>
                        </AppIcon>
                      );
                    })
                  }
                </IconWrapper>
              </Section>
              {
                !['spotify', 'other_apps'].includes(this.selectedApp) &&
                  <Section>
                    <h3>Viewing this page on your phone?</h3>
                    <Note marginBottom='10px'>Just tap the button to subscribe</Note>
                    <Button
                      onClick={this.openInSelectedApp}
                      background={AppUI.site.accentColor}
                    >
                      {`Open in ${this.currentApp['name']}`}
                    </Button>
                  </Section>
              }
              {
                !['spotify', 'other_apps'].includes(this.selectedApp) &&
                  <Section>
                    <h3>Viewing this page on your computer?</h3>
                    <Note marginBottom='10px'>Scan this QR code with your phone</Note>
                    <QRCodeSVG value={this.currentAppUrl}/>
                  </Section>
              }
              {
                this.selectedApp === 'spotify' &&
                  <Section>
                    <Text>Unfortunately, it's not currently possible to subscribe to the members-only feed in Spotify.</Text>
                    <Text>We encourage you to try the apps listed here instead.</Text>
                  </Section>
              }
              {
                this.selectedUserPodcast && this.selectedApp === 'other_apps' &&
                  <Section>
                    <h3>Your RSS feed URL</h3>
                    <Note fontStyle='none'>Most podcast apps have an option to subscribe to a podcast feed by URL.</Note>
                    <Note fontStyle='none'>You can copy this link and paste it into the appropriate field in your podcast app of choice.</Note>
                    <InputRow>
                      <InputWrapper>
                        <input value={this.selectedUserPodcast.rssFeedUrl}/>
                      </InputWrapper>
                      <Copy
                        hideIcon
                        text={this.selectedUserPodcast.rssFeedUrl}
                      >
                        <Button background={AppUI.site.accentColor}>Copy Link</Button>
                      </Copy>
                    </InputRow>
                    <Note marginTop='20px'>Note that apps not listed on this page may not support chapters and chapter images.</Note>
                  </Section>
              }
          </InnerWrapper>
        </RelativeWrapper>
      </div>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(RssFeedIndex);
